// Client-safe catalogue: three skulls, three colourways each, one SKU per colourway. The editorial
// half lives here; catalog.service.ts overlays live price, stock and status from the database.

export type ColourwayId = "blaze" | "olive" | "ghost"

export type Colourway = {
  id: ColourwayId
  name: string
  sku: string
  /** Swatch token: matches the filament, not the UI palette. */
  hex: string
  /** The print file rendered in this filament, on a transparent ground. */
  swatch: string
  blurb: string
  image: string
  /** Wire-shaped money. A fallback: the live per-variant price is overlaid from the database. */
  price: string
  /** Units on hand, overlaid the same way. */
  stock: number
  inStock: boolean
  bestSeller?: boolean
}

export type Product = {
  slug: string
  name: string
  /** What the thing is, as the product information on its page declares it. */
  productType: string
  strapline: string
  /** On the lineup and shop cards: two lines on a phone or a laptop, so keep it near 70 characters. */
  summary: string
  /** The page's meta description. Says only what this skull's own specs support. */
  searchDescription: string
  /** Under the price: what the thing is, in the words people search for. */
  intro: string
  /** Wire-shaped money: a string. */
  price: string
  compareAtPrice: string
  /** 0 with `reviewCount` 0 for a product nobody has reviewed yet: the page shows no stars. */
  rating: number
  reviewCount: number
  unitsLeft: number
  colourways: Colourway[]
  /** Each finish's own pictures, in gallery order. A Record, so no finish is left without any. */
  gallery: Record<ColourwayId, GalleryImage[]>
  /** The three finishes in one picture, for its combo packs. */
  packImage: string
  specs: Array<{ label: string; value: string; pending?: boolean }>
  inTheBox: string[]
  sections: ProductSections
}

/** A picture as a section shows it. */
export type Picture = {
  src: string
  alt: string
  /** Where a crop keeps it, as CSS object-position, when the centre would cut the skull. */
  position?: string
  /** Closer than the frame, as a CSS scale from the top edge, when the skull sits small in it. */
  zoom?: number
}

/** FAQ answers that differ for one skull, by question. null leaves the question out. */
export type FaqAnswers = Record<string, string | null>

/** What the product page's sections show of this skull. Home and about show the Flame Skull's. */
export type ProductSections = {
  /** "More than a mount": one picture per card, in the cards' order. */
  inUse: [Picture, Picture, Picture]
  /** "The build": the picture beside the specs, and what it says above them. */
  build: { picture: Picture; body: string }
  /** "The finish": the close-up, and what it says about the print. Without it, no section. */
  finish?: { picture: Picture; body: string }
  /** "Install": the mount going up on a wall. */
  install: Picture
  /** "The alternatives": what it looks like, in the comparison's last row. */
  looksLike: string
  /** Beside the questions. */
  faq: Picture
  /** Where the questions' answers are not true of this skull, or not yet known. */
  faqAnswers?: FaqAnswers
}

/** One picture, in one finish, as the gallery shows it. */
export type GalleryImage = {
  src: string
  alt: string
  /** For a shot of the mount in use: what it is showing, over the image. */
  caption?: { title: string; body: string }
}

// One shot in all three finishes. The files share framing and size, so switching does not shift it.
type GalleryShot = Omit<GalleryImage, "src"> & { src: Record<ColourwayId, string> }

function inFinish(shot: GalleryShot, id: ColourwayId): GalleryImage {
  return { ...shot, src: shot.src[id] }
}

export const COLOURWAYS: Colourway[] = [
  {
    id: "blaze",
    name: "Blaze Orange",
    sku: "SKM-BLZ",
    hex: "#FF5A1F",
    swatch: "/product/swatch-3d-blaze.png",
    blurb: "Bright and hot, built to catch the eye.",
    // The 3D skull seated in the front photo, as olive and grey are, so all three match.
    image: "/product/colourway-blaze-print.jpg",
    price: "3499",
    stock: 0,
    inStock: true,
    bestSeller: true,
  },
  {
    id: "olive",
    name: "Militia Olive",
    sku: "SKM-OLV",
    hex: "#8A9A5B",
    swatch: "/product/swatch-3d-olive.png",
    blurb: "Bold in presence, subtle in colour.",
    image: "/product/colourway-olive-print.jpg",
    price: "3499",
    stock: 0,
    inStock: true,
  },
  {
    id: "ghost",
    name: "Ghost Grey",
    sku: "SKM-GHT",
    // The grey the real print shows: near neutral, a faint violet cast.
    hex: "#98979E",
    swatch: "/product/swatch-3d-ghost.png",
    blurb: "Calm, cold and still as stone.",
    image: "/product/colourway-ghost-grey-print.jpg",
    price: "3499",
    stock: 0,
    inStock: true,
  },
]

/** Far stop of each colourway's button gradient on a card; the swatch hex is the near one. */
export const TINT_TO: Record<ColourwayId, string> = {
  blaze: "#ff8a00",
  olive: "#aab872",
  ghost: "#bebdc4",
}

const FRONT: GalleryShot = {
  alt: "Flame Skull helmet wall mount, front elevation",
  src: {
    blaze: "/product/colourway-blaze-print.jpg",
    olive: "/product/colourway-olive-print.jpg",
    ghost: "/product/colourway-ghost-grey-print.jpg",
  },
}

// The owner's photographs in Blaze Orange; olive and grey recolour only the skull.
const FITTING: GalleryShot = {
  alt: "Fixing the Flame Skull helmet wall mount to the wall with a screwdriver, the skull already on its arm",
  src: {
    blaze: "/product/gallery-fitting.jpg",
    olive: "/product/gallery-fitting-olive.jpg",
    ghost: "/product/gallery-fitting-ghost-grey.jpg",
  },
}

const PLACING_HELMET: GalleryShot = {
  alt: "Setting a black helmet onto the Flame Skull helmet wall mount",
  src: {
    blaze: "/product/gallery-placing-helmet.jpg",
    olive: "/product/gallery-placing-helmet-olive.jpg",
    ghost: "/product/gallery-placing-helmet-ghost-grey.jpg",
  },
}

const HANGING_JACKET: GalleryShot = {
  alt: "Hanging a riding jacket and gloves on the hook under a helmet on the helmet wall mount",
  src: {
    blaze: "/product/gallery-hanging-jacket.jpg",
    olive: "/product/gallery-hanging-jacket-olive.jpg",
    ghost: "/product/gallery-hanging-jacket-ghost-grey.jpg",
  },
}

const GEAR_LABELS: GalleryShot = {
  alt: "A helmet on the mount, gloves and a jacket on its hooks, labelled: keeps the helmet organised, prevents scratches and damage, improves airflow so it dries faster, holds gloves, extra hook for jackets and gear",
  src: {
    blaze: "/product/gallery-gear-labels.jpg",
    olive: "/product/gallery-gear-labels-olive.jpg",
    ghost: "/product/gallery-gear-labels-ghost-grey.jpg",
  },
}

const GARAGE_BIKE: GalleryShot = {
  alt: "Flame Skull helmet wall mount on a lit garage wall, a helmet on it and a sports bike parked below",
  src: {
    blaze: "/product/gallery-garage-bike.jpg",
    olive: "/product/gallery-garage-bike-olive.jpg",
    ghost: "/product/gallery-garage-bike-ghost-grey.jpg",
  },
}

const INSTALL: GalleryShot = {
  alt: "Screwing the mount to the wall through the foot of its plate",
  caption: {
    title: "Up in four steps",
    body: "Mark, drill, plug, screw. The template, screws and wall plugs are in the box.",
  },
  src: {
    blaze: "/product/gallery-install.jpg",
    olive: "/product/gallery-install-olive.jpg",
    ghost: "/product/gallery-install-ghost-grey.jpg",
  },
}

const FLAME_DETAIL: GalleryShot = {
  alt: "Macro detail of the carved flame relief",
  src: {
    blaze: "/product/detail-flame.jpg",
    olive: "/product/detail-flame-olive.jpg",
    ghost: "/product/detail-flame-ghost-grey.jpg",
  },
}

/** The gallery's order, the same in every finish. */
const SHOTS = [
  FITTING,
  PLACING_HELMET,
  HANGING_JACKET,
  GEAR_LABELS,
  GARAGE_BIKE,
  INSTALL,
  FLAME_DETAIL,
  FRONT,
]

/** "The finish": the Flame Skull's alone; home and about show it too. */
export const FLAME_FINISH = {
  picture: {
    src: "/product/detail-flame.jpg",
    alt: "Macro detail of the carved flame relief and 3D-print layer lines",
  },
  body: "We don't sand the print smooth and pretend it was moulded. On this 3D-printed helmet wall mount, the fine horizontal ridges catch the light, the flame valleys go properly deep, and the whole thing reads as made rather than manufactured.",
} satisfies ProductSections["finish"]

export const FLAME_SKULL_MOUNT: Product = {
  slug: "flame-skull-mount",
  name: "Flame Skull Helmet Wall Mount",
  productType: "Wall-mounted helmet holder (Flame Skull helmet wall mount)",
  strapline: "It earned every scratch. Give it a wall, not the floor.",
  summary:
    "Carved flames, bared teeth. A wall-mounted helmet holder: helmet up top, gloves and keys below.",
  searchDescription:
    "Wall-mounted bike helmet holder shaped like a flame skull. Fits full-face, open-face and modular helmets, plus gloves and keys. 10 kg rated. Made in India.",
  intro:
    "The Flame Skull is a helmet holder for wall mounting in your garage, bedroom or living room. Use it as a bike helmet stand that also holds your gloves, jacket and keys, and it fits full-face, open-face and modular helmets.",
  price: "3499",
  compareAtPrice: "4499",
  rating: 4.9,
  reviewCount: 312,
  unitsLeft: 12,
  colourways: COLOURWAYS,
  gallery: {
    blaze: SHOTS.map((shot) => inFinish(shot, "blaze")),
    olive: SHOTS.map((shot) => inFinish(shot, "olive")),
    ghost: SHOTS.map((shot) => inFinish(shot, "ghost")),
  },
  packImage: "/product/combo-flame-skull-trio.webp",
  specs: [
    { label: "Material", value: "PLA+ · matte" },
    { label: "Load rating", value: "10 kg" },
    { label: "Weight", value: "315 g" },
    { label: "Fixings", value: "3 × screws + wall plugs" },
    { label: "Fits", value: "Full-face, open-face and modular" },
  ],
  // Matches the box picture in The build section (anatomy.tsx).
  inTheBox: [
    "Skull mount, arm attached",
    "3 × screws + wall plugs",
    "Drilling template",
    "Thank-you card",
    "Mystery box",
  ],
  sections: {
    // One in each colourway.
    inUse: [
      {
        src: "/product/gallery-wall-gear-ghost-grey.jpg",
        alt: "A helmet on a Ghost Grey mount, a jacket, gloves and keys on its hooks",
      },
      {
        src: "/product/gallery-garage-night.jpg",
        alt: "The mount in a garage at night, a white open-face helmet on the skull",
      },
      {
        src: "/product/rider-cream-helmet.jpg",
        alt: "A Militia Olive mount wearing a cream open-face helmet, gloves and a jacket below",
      },
    ],
    build: {
      picture: {
        src: "/product/box-contents.jpg",
        alt: "What comes in the box: the flame skull on its arm in one piece, a paper drilling guide marking the three holes, three screws and wall plugs, a thank-you card and a mystery box",
      },
      body: "The helmet wall mount arm fixes to the wall with 3 screws. The skull is shaped to fit into any helmet type and size. The whole mount supports up to 10 kg.",
    },
    finish: FLAME_FINISH,
    // Shipped in one piece, so it goes up whole.
    install: {
      src: "/product/why-install-olive.jpg",
      alt: "Screwing a Militia Olive SKELMET mount to a wall through the foot of its plate, the skull already fixed to its arm",
    },
    looksLike: "A flaming skull",
    faq: {
      src: "/product/gallery-bare-skull-ghost-grey.jpg",
      alt: "The SKELMET mount in Ghost Grey on its black arm, gloves hanging from the hook",
    },
  },
}

// ── the Piston Skull ────────────────────────────────────────

// The Flame Skull's filament, name and colour, with this skull's SKU and pictures.
function pistonFinish(
  id: ColourwayId,
  sku: string,
  files: { image: string; swatch: string },
): Colourway {
  const { name, hex, blurb } = COLOURWAYS.find((c) => c.id === id)!
  // Sold out unless the database says otherwise: a page built without its rows must not sell it.
  return { id, name, hex, blurb, sku, ...files, price: "3499", stock: 0, inStock: false }
}

// Card pictures: the front shot, its colour matched to the Flame Skull's (build-piston-cards.mjs).
export const PISTON_COLOURWAYS: Colourway[] = [
  pistonFinish("blaze", "SKM-PST-BLZ", {
    image: "/product/piston-card.jpg",
    swatch: "/product/swatch-3d-piston-blaze.png",
  }),
  pistonFinish("olive", "SKM-PST-OLV", {
    image: "/product/piston-card-olive.jpg",
    swatch: "/product/swatch-3d-piston-olive.png",
  }),
  pistonFinish("ghost", "SKM-PST-GHT", {
    image: "/product/piston-card-ghost-grey.jpg",
    swatch: "/product/swatch-3d-piston-ghost.png",
  }),
]

// The owner's photos of the mount in use; olive and grey are the orange recoloured
// (scripts/build-piston-colourways.mjs).
const pistonShot = (name: string, alt: string, caption?: GalleryShot["caption"]): GalleryShot => ({
  alt,
  ...(caption ? { caption } : {}),
  src: {
    blaze: `/product/piston-${name}.jpg`,
    olive: `/product/piston-${name}-olive.jpg`,
    ghost: `/product/piston-${name}-ghost-grey.jpg`,
  },
})

// In the Flame Skull's order.
const PISTON_SHOTS = [
  pistonShot(
    "fitting",
    "Fixing the mount to the wall with a screwdriver, the skull already on its arm",
    {
      title: "Up in four steps",
      body: "Mark, drill, plug, screw. The template, screws and wall plugs are in the box.",
    },
  ),
  pistonShot("placing-helmet", "Setting a helmet onto the piston skull on its wall mount"),
  pistonShot("wall-gear", "A helmet on the mount, gloves hanging from the hook under its arm"),
  pistonShot("garage-bike", "A helmet on the mount on a lit wall, a sports bike parked below it"),
  pistonShot(
    "helmet-held",
    "A helmet on the piston skull, face on, the piston showing through its opening",
  ),
  pistonShot("back", "From behind: the mohawk running down the back of the skull"),
]

export const PISTON_SKULL_MOUNT: Product = {
  slug: "piston-skull-mount",
  name: "Piston Skull Helmet Wall Mount",
  productType: "Wall-mounted helmet holder (Piston Skull helmet wall mount)",
  strapline: "Mohawk up, piston in its teeth. A wall for the lid that earned it.",
  summary:
    "Mohawk up, piston in its teeth. A wall-mounted helmet holder: helmet up top, gloves on the hook.",
  searchDescription:
    "Wall-mounted bike helmet holder shaped like a mohawk skull with a piston in its teeth. Hooks for your gloves, three colours. Made in India.",
  intro:
    "The Piston Skull is a helmet holder for wall mounting, with a mohawk, a piston in its teeth and a hook for your gloves. A bike helmet stand that keeps your helmet off the floor.",
  // Owner's price. Live price and stock come from the variant rows.
  price: "3499",
  compareAtPrice: "4499",
  // No reviews yet, so no stars.
  rating: 0,
  reviewCount: 0,
  unitsLeft: 0,
  colourways: PISTON_COLOURWAYS,
  gallery: {
    blaze: PISTON_SHOTS.map((shot) => inFinish(shot, "blaze")),
    olive: PISTON_SHOTS.map((shot) => inFinish(shot, "olive")),
    ghost: PISTON_SHOTS.map((shot) => inFinish(shot, "ghost")),
  },
  packImage: "/product/combo-piston-skull-trio.webp",
  // Fit confirmed by the owner (2026-10-08). Weight and load rating (its own bracket) still wait.
  specs: [
    { label: "Material", value: "PLA+ · matte" },
    { label: "Skull", value: "15 × 28 × 21 cm" },
    { label: "Fixings", value: "3 × screws + wall plugs" },
    { label: "Fits", value: "Full-face and open-face" },
  ],
  inTheBox: [
    "Skull mount, arm attached",
    "3 × screws + wall plugs",
    "Drilling template",
    "Thank-you card",
    "Mystery box",
  ],
  sections: {
    inUse: [
      {
        src: "/product/piston-wall-gear-ghost-grey.jpg",
        alt: "A helmet on a Ghost Grey piston skull mount, gloves hanging from the hook under its arm",
      },
      {
        src: "/product/piston-garage-bike.jpg",
        alt: "A helmet on the mount on a lit wall, a sports bike parked below it",
      },
      {
        src: "/product/piston-helmet-held-olive.jpg",
        alt: "A helmet on a Militia Olive piston skull, the piston showing through its opening",
      },
    ],
    build: {
      picture: {
        src: "/product/piston-back-wide.jpg",
        alt: "The piston skull on its black arm, from behind, the mohawk running down its back",
      },
      body: "The helmet wall mount arm fixes to the wall with 3 screws. The skull takes full-face and open-face helmets, and rides on its post facing out from the wall.",
    },
    install: {
      src: "/product/piston-fitting-olive.jpg",
      alt: "Screwing a Militia Olive piston skull mount to a wooden wall, the skull already on its arm",
      position: "50% 40%",
    },
    looksLike: "A mohawk skull, piston in its teeth",
    faq: {
      src: "/product/piston-back-ghost-grey.jpg",
      alt: "The Piston Skull Helmet Wall Mount in Ghost Grey, from behind",
    },
    // Fit confirmed for full-face and open-face only; the load rating is still unconfirmed.
    faqAnswers: {
      "Will the SKELMET mount hold a full-face helmet?":
        "Yes. Full-face and open-face helmets sit on it. The skull goes inside the helmet and spreads its weight across the liner, so no single spot of the padding is pressed out of shape.",
      "Can the SKELMET mount hold my jacket and gloves as well as my helmet?":
        "Yes. The arm has hooks under the skull, so your gloves, jacket and keys hang right below your helmet.",
      "Can I put up the SKELMET mount without drilling?":
        "We do not recommend it. The mount is made to be screwed into the wall with the three screws and wall plugs in the box. Adhesive strips and hooks are not made for a helmet's weight, and the mount could come down. The paper guide in the box marks exactly where to drill.",
    },
  },
}

// ── the Aviator Skull ───────────────────────────────────────

// Cards: the front photo cut out onto the Piston's dark backdrop; swatches the same skull alone
// (scripts/build-aviator-pictures.mjs).
export const AVIATOR_COLOURWAYS: Colourway[] = [
  pistonFinish("blaze", "SKM-AVT-BLZ", {
    image: "/product/aviator-card.jpg",
    swatch: "/product/swatch-3d-aviator-blaze.png",
  }),
  pistonFinish("olive", "SKM-AVT-OLV", {
    image: "/product/aviator-card-olive-3.jpg",
    swatch: "/product/swatch-3d-aviator-olive-3.png",
  }),
  pistonFinish("ghost", "SKM-AVT-GHT", {
    image: "/product/aviator-card-ghost-grey-3.jpg",
    swatch: "/product/swatch-3d-aviator-ghost-3.png",
  }),
]

/**
 * The owner's photos (2026-10-27).
 * - `recoloured`: the orange photo put into the other print, with this skull's own prints
 *   measured off the picture of all three (build-aviator-pictures.mjs).
 * - `asShot`: the two pictures of all three prints, the same in every finish.
 * - `blazeOnly`: left out of the other galleries. The shelf gives the orange skull a
 *   reflection as deeply orange as the print itself, so no recolour of it looks real.
 */
const aviatorShot = (
  name: string,
  alt: string,
  how: "recoloured" | "asShot" | "blazeOnly",
): GalleryShot => ({
  alt,
  src: {
    blaze: `/product/aviator-${name}.jpg`,
    olive:
      how === "recoloured"
        ? `/product/aviator-${name}-olive-3.jpg`
        : how === "asShot"
          ? `/product/aviator-${name}.jpg`
          : "",
    ghost:
      how === "recoloured"
        ? `/product/aviator-${name}-ghost-grey-3.jpg`
        : how === "asShot"
          ? `/product/aviator-${name}.jpg`
          : "",
  },
})

const AVIATOR_SHOTS = [
  aviatorShot("wall", "The aviator skull on its black arm on a sunlit wall, face on", "recoloured"),
  aviatorShot(
    "helmet",
    "A full-face helmet on the mount, the skull's face showing through its opening",
    "recoloured",
  ),
  aviatorShot("side", "The aviator skull from three-quarters: cap, goggles and cigar", "blazeOnly"),
  aviatorShot("trio", "The aviator skull in Militia Olive, Blaze Orange and Ghost Grey", "asShot"),
  aviatorShot("lineup", "The three colourways side by side on a counter", "asShot"),
]

const aviatorGallery = (id: ColourwayId): GalleryImage[] =>
  AVIATOR_SHOTS.filter((shot) => shot.src[id]).map((shot) => inFinish(shot, id))

export const AVIATOR_SKULL_MOUNT: Product = {
  slug: "aviator-skull-mount",
  name: "Aviator Skull Helmet Wall Mount",
  productType: "Wall-mounted helmet holder (Aviator Skull helmet wall mount)",
  strapline: "Goggles up, cigar in its teeth. A wall for the lid that earned it.",
  summary:
    "Leather cap, goggles up, cigar in its teeth. A wall-mounted helmet holder: helmet up top, off the floor.",
  searchDescription:
    "Wall-mounted bike helmet holder shaped like an aviator skull with leather cap, goggles and cigar. Keeps your helmet off the floor, three colours. Made in India.",
  intro:
    "The Aviator Skull is a helmet holder for wall mounting, in a leather cap and goggles with a cigar in its teeth. Use it as a bike helmet stand that keeps your helmet off the floor.",
  // The Piston Skull's price (owner, 2026-10-27). Live price and stock come from the variant rows.
  price: "3499",
  compareAtPrice: "4499",
  // No reviews yet, so no stars.
  rating: 0,
  reviewCount: 0,
  unitsLeft: 0,
  colourways: AVIATOR_COLOURWAYS,
  gallery: {
    blaze: aviatorGallery("blaze"),
    olive: aviatorGallery("olive"),
    ghost: aviatorGallery("ghost"),
  },
  packImage: "/product/aviator-trio.jpg",
  // Seen holding a full-face helmet in the owner's photo. Open-face, weight, size and a load
  // rating are not confirmed yet.
  specs: [
    { label: "Material", value: "PLA+ · matte" },
    { label: "Fixings", value: "Screws + wall plugs" },
    { label: "Fits", value: "Full-face" },
  ],
  inTheBox: [
    "Skull mount, arm attached",
    "Screws + wall plugs",
    "Drilling template",
    "Thank-you card",
    "Mystery box",
  ],
  sections: {
    inUse: [
      {
        src: "/product/aviator-lineup.jpg",
        alt: "The aviator skull in Militia Olive, Blaze Orange and Ghost Grey on a counter",
      },
      {
        src: "/product/aviator-helmet-ghost-grey-3.jpg",
        alt: "A full-face helmet on a Ghost Grey aviator skull mount",
      },
      {
        src: "/product/aviator-wall-olive-3.jpg",
        alt: "A Militia Olive aviator skull on its black arm on a sunlit wall",
        zoom: 1.25,
      },
    ],
    build: {
      picture: {
        src: "/product/aviator-trio-wide.jpg",
        alt: "The aviator skull in Militia Olive, Blaze Orange and Ghost Grey, side by side",
      },
      body: "The mount arm screws to the wall. The skull, in its leather cap and goggles, goes inside a full-face helmet and holds it face out from the wall.",
    },
    install: {
      src: "/product/aviator-wall.jpg",
      alt: "The Blaze Orange aviator skull up on a wall on its black arm",
      position: "50% 0%",
    },
    looksLike: "A skull in an aviator's cap and goggles",
    faq: {
      src: "/product/aviator-side.jpg",
      alt: "The Aviator Skull Helmet Wall Mount in Blaze Orange, from three-quarters",
    },
    // Only what the owner's photos show: a full-face helmet. Hooks and a load rating unconfirmed.
    faqAnswers: {
      "Will the SKELMET mount hold a full-face helmet?":
        "Yes. A full-face helmet sits on it. The skull goes inside the helmet and spreads its weight across the liner, so no single spot of the padding is pressed out of shape.",
      "Can the SKELMET mount hold my jacket and gloves as well as my helmet?": null,
      "What comes in the SKELMET box?":
        "The skull, already fixed to its black arm in one piece, a paper drilling guide, screws with wall plugs, a thank-you card and a mystery box.",
      "Can I put up the SKELMET mount without drilling?":
        "We do not recommend it. The mount is made to be screwed into the wall with the screws and wall plugs in the box. Adhesive strips and hooks are not made for a helmet's weight, and the mount could come down. The paper guide in the box marks exactly where to drill.",
    },
  },
}

export const PRODUCTS: Product[] = [FLAME_SKULL_MOUNT, PISTON_SKULL_MOUNT, AVIATOR_SKULL_MOUNT]

export function getProduct(slug: string): Product | undefined {
  return PRODUCTS.find((p) => p.slug === slug)
}

/** A colourway of a product: the Flame Skull's unless another is named. */
export function getColourway(
  id: string,
  product: Product = FLAME_SKULL_MOUNT,
): Colourway | undefined {
  return product.colourways.find((c) => c.id === id)
}

/** Falls back to the first colourway rather than throwing on a bad query param. */
export function resolveColourway(
  id: string | undefined,
  product: Product = FLAME_SKULL_MOUNT,
): Colourway {
  const found = id ? getColourway(id, product) : undefined
  return found ?? product.colourways[0]!
}
